import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const offices = [
  {
    name: "Main Office - Texas",
    address: "16475 N Dallas Pky, Suite 745",
    city: "Addison, TX 75001",
    phone: "(866) 841-2149",
    email: "info@accessmedicalgroup.online",
  },
  {
    name: "Delaware Office",
    address: "1313 N Market St, Suite 100",
    city: "Wilmington, DE 19801",
    phone: "(866) 841-2149",
    email: "info@accessmedicalgroup.online",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#14213D] py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 bg-[#00B4D8]/20 text-[#00B4D8] text-sm font-medium rounded-full mb-6">
              Contact Us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Have questions about our services or need assistance? Our team at Medical Access Inc is 
              here to help. Reach out today and let us know how we can serve you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-[#F1FAEE]" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 -mt-20">
            {[
              { icon: Phone, title: "Call Us", info: "(866) 841-2149", sub: "Mon-Fri 9AM-5PM, Sat-Sun 11AM-4PM" },
              { icon: Mail, title: "Email Us", info: "info@accessmedicalgroup.online", sub: "We respond within 24 hours" },
              { icon: Clock, title: "Office Hours", info: "Mon-Fri: 9AM-5PM", sub: "Sat-Sun: 11AM-4PM" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 text-center shadow-lg"
                data-testid={`contact-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#00B4D8]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#00B4D8]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#14213D] mb-1">
                  {item.title}
                </h3>
                <p className="text-[#14213D] font-medium">{item.info}</p>
                <p className="text-gray-500 text-sm">{item.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Offices */}
      <section className="py-20 bg-[#F1FAEE]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg"
              data-testid="contact-form"
            >
              <h2 className="font-heading text-2xl font-bold text-[#14213D] mb-2">
                Send Us a Message
              </h2>
              <p className="text-gray-600 mb-6">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      className="rounded-lg"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="rounded-lg"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="rounded-lg"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Select
                      value={formData.subject}
                      onValueChange={(value) => setFormData({ ...formData, subject: value })}
                    >
                      <SelectTrigger className="rounded-lg" data-testid="contact-subject">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                        <SelectItem value="Find a Provider">Find a Provider</SelectItem>
                        <SelectItem value="Insurance Questions">Insurance Questions</SelectItem>
                        <SelectItem value="Patient Support">Patient Support</SelectItem>
                        <SelectItem value="Careers">Careers</SelectItem>
                        <SelectItem value="Partnership">Partnership</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    className="rounded-lg"
                    rows={5}
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#00B4D8] text-white rounded-lg py-6 hover:bg-[#0096c7] transition-colors"
                  data-testid="contact-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </motion.div>

            {/* Office Locations */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl font-bold text-[#14213D] mb-6">
                Our Offices
              </h2>
              <div className="space-y-6">
                {offices.map((office, index) => (
                  <div
                    key={office.name}
                    className="bg-white rounded-xl p-6 card-hover"
                    data-testid={`office-${index}`}
                  >
                    <h3 className="font-heading font-semibold text-lg text-[#14213D] mb-4">
                      {office.name}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-[#00B4D8] mt-0.5 flex-shrink-0" />
                        <div className="text-gray-600 text-sm">
                          {office.address}<br />
                          {office.city}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-[#00B4D8] flex-shrink-0" />
                        <a href={`tel:${office.phone}`} className="text-gray-600 text-sm hover:text-[#00B4D8]">
                          {office.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-[#00B4D8] flex-shrink-0" />
                        <a href={`mailto:${office.email}`} className="text-gray-600 text-sm hover:text-[#00B4D8]">
                          {office.email}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hours */}
              <div className="mt-6 bg-[#14213D] rounded-xl p-6 text-white">
                <h3 className="font-heading font-semibold text-lg mb-4">Hours of Operation</h3>
                <div className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday - Sunday</span>
                    <span>11:00 AM - 4:00 PM</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-20 bg-white" data-testid="faq-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#14213D] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Looking for quick answers? Check out our FAQ section for common 
              questions about Medical Access Inc services, insurance, and patient care.
            </p>
            <Button
              className="bg-[#00B4D8] text-white rounded-lg px-6 py-5 hover:bg-[#0096c7]"
              data-testid="view-faq"
            >
              View FAQs
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
